import type {CSSProperties} from 'react';
import {weight} from '../scale.js';
import {wedgeExactRole} from './wedgeRoles.js';

export type WedgeSearchTabsLook = 'searchtab-pill';

export const isWedgeSearchTabsLook = (look: string): look is WedgeSearchTabsLook => look === 'searchtab-pill';

/* Released Search mode pill (the Type and Scan track of the PREP search surface), off-scale. It is a flex box that centers its icon and label
   with a 5px gap, `8px 0` padding, a 10px radius, 12.5px semibold type in the Wedge font, no border. The fill, the ink and the lift shadow
   read the --wedge-search-tab-bg, --wedge-search-tab-ink and --wedge-search-tab-lift roles: the caller sets them per pill (the surface fill,
   the app ink and a soft shadow when chosen; no fill, the secondary ink and no shadow when not; light-on-black values in card mode). The
   pill settles over 200ms on the fill, the ink and the shadow. It draws no transparent target (wedgeSearchTabsDrawsHit): the pills sit 0px
   apart in one 3px-padded track, so a 44px region would overlap the neighbor and would add a positioned box that legacy does not have. */
const role = (name: string, fallback: string): string => `var(--wedge-search-tab-${name}, ${fallback})`;
export const wedgeSearchTabsDrawsHit = (look: string): boolean => !isWedgeSearchTabsLook(look);

export function wedgeSearchTabsStyle(_look: WedgeSearchTabsLook): CSSProperties {
  const ease = wedgeExactRole('ease');
  return {
    padding: '8px 0', background: role('bg', 'transparent'), color: role('ink', 'inherit'), border: 0, borderRadius: 10, cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
    fontSize: 12.5, fontWeight: weight.semibold, fontFamily: wedgeExactRole('font'),
    transition: `background 200ms ${ease}, color 200ms ${ease}, box-shadow 200ms ${ease}`,
    boxShadow: role('lift', 'none'),
    WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation',
  };
}
